import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CookieConsent from "./CookieConsent";
import AnimatedOutlet from "./AnimatedOutlet";
import ScrollProgress from "./ScrollProgress";
import AutoTranslator from "./AutoTranslator";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { supabase } from "@/integrations/supabase/client";
import Maintenance from "@/pages/Maintenance";

const Layout = () => {
  useSmoothScroll();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);

  // Track admin session (any logged-in user = admin in this project)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setAuthReady(true);
    });
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setAuthReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["site-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("maintenance_mode, maintenance_message")
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
  });

  // Realtime: react to changes immediately
  useEffect(() => {
    const channel = supabase
      .channel("site-settings-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "site_settings" },
        () => queryClient.invalidateQueries({ queryKey: ["site-settings"] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Avoid flash: wait for both queries before deciding
  if (!authReady || settingsLoading) {
    return <div className="min-h-screen bg-background" />;
  }

  const isAdmin = !!session;
  const maintenance = !!settings?.maintenance_mode;

  if (maintenance && !isAdmin) {
    return <Maintenance message={settings?.maintenance_message} />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <ScrollProgress />
      <main className="flex-1 pt-16">
        <AnimatedOutlet />
      </main>
      <Footer />
      <CookieConsent />
      <AutoTranslator />
    </div>
  );
};

export default Layout;
